import { getGraphQLClient } from '../graphql';

export interface TranslationSegment {
  id: string;
  externalId: string;
  position: number;
  blockType: string;
  sourceText: string | null;
  targetText: string | null;
  status: string;
  conflicted: boolean;
  conflictRepoText: string | null;
  audioStatus: string;
  audioUrl: string | null;
  needsAudioDecision: boolean;
}

export interface TranslationChapter {
  id: string;
  position: number;
  title: string | null;
  displayTitle: string;
  segmentCount: number;
  translatedCount: number;
  translating: boolean;
  segments: TranslationSegment[];
}

export interface TranslationReadingPosition {
  segmentExternalId: string;
  charOffset: number;
}

export interface TranslationProject {
  id: string;
  title: string;
  slug: string;
  sourceKind: string;
  sourceLanguage: string;
  targetLanguage: string;
  status: string;
  autoTranslate: boolean;
  glossaryMd: string | null;
  rulesMd: string | null;
  styleMd: string | null;
  narrationStyle: string | null;
  pronunciationMd: string | null;
  githubRepoUrl: string | null;
  githubSyncConfigured: boolean;
  conflictCount: number;
  segmentCount: number;
  translatedCount: number;
  chapters: TranslationChapter[];
  readingPosition: TranslationReadingPosition | null;
}

export interface TranslationProjectSummary {
  id: string;
  title: string;
  sourceLanguage: string;
  targetLanguage: string;
  status: string;
  segmentCount: number;
  translatedCount: number;
  conflictCount: number;
}

const PROJECT_FIELDS = `
  id title slug sourceKind sourceLanguage targetLanguage status autoTranslate
  glossaryMd rulesMd styleMd narrationStyle pronunciationMd
  githubRepoUrl githubSyncConfigured conflictCount segmentCount translatedCount
  readingPosition { segmentExternalId charOffset }
  chapters {
    id position title displayTitle segmentCount translatedCount translating
    segments {
      id externalId position blockType sourceText targetText status
      conflicted conflictRepoText audioStatus audioUrl needsAudioDecision
    }
  }
`;

export async function fetchTranslationProjects(): Promise<TranslationProjectSummary[]> {
  const query = `query {
    translationProjects {
      id title sourceLanguage targetLanguage status segmentCount translatedCount conflictCount
    }
  }`;
  const data = await getGraphQLClient().request<{ translationProjects: TranslationProjectSummary[] }>(query);
  return data.translationProjects;
}

export async function fetchTranslationProject(id: string): Promise<TranslationProject | null> {
  const query = `query($id: ID!) { translationProject(id: $id) { ${PROJECT_FIELDS} } }`;
  const data = await getGraphQLClient().request<{ translationProject: TranslationProject | null }>(query, { id });
  return data.translationProject;
}

export async function createTranslationProject(input: {
  sourceBookId: string;
  targetLanguage: string;
  title?: string;
  sourceLanguage?: string;
}): Promise<{ success: boolean; errors: string[]; project: { id: string } | null }> {
  const query = `mutation($input: CreateTranslationProjectInput!) {
    createTranslationProject(input: $input) { success errors project { id } }
  }`;
  const data = await getGraphQLClient().request<{
    createTranslationProject: { success: boolean; errors: string[]; project: { id: string } | null };
  }>(query, { input });
  return data.createTranslationProject;
}

export async function importFromGithub(
  repoName: string,
): Promise<{ success: boolean; errors: string[]; project: { id: string } | null }> {
  const query = `mutation($input: ImportTranslationFromGithubInput!) {
    importTranslationFromGithub(input: $input) { success errors project { id } }
  }`;
  const data = await getGraphQLClient().request<{
    importTranslationFromGithub: { success: boolean; errors: string[]; project: { id: string } | null };
  }>(query, { input: { repoName } });
  return data.importTranslationFromGithub;
}

// Uploads an EPUB via the multipart endpoint, returning the new project id.
export async function uploadEpub(file: File, targetLanguage: string): Promise<string> {
  const token = localStorage.getItem('token');
  const form = new FormData();
  form.append('file', file);
  form.append('target_language', targetLanguage);
  const response = await fetch(`${window.location.origin}/translate/uploads`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Upload failed');
  return json.id as string;
}

export async function configureSync(projectId: string, repoName?: string): Promise<void> {
  const query = `mutation($input: ConfigureTranslationSyncInput!) {
    configureTranslationSync(input: $input) { success errors }
  }`;
  await getGraphQLClient().request(query, { input: { projectId, repoName } });
}

// Per-project guide overrides editable from the settings page. Omitting a field
// leaves it unchanged; an empty string clears the override (inherits the default).
export interface ProjectDocs {
  glossaryMd?: string;
  rulesMd?: string;
  styleMd?: string;
  narrationStyle?: string;
  pronunciationMd?: string;
}

export async function updateProjectDocs(projectId: string, docs: ProjectDocs): Promise<void> {
  const query = `mutation($input: UpdateTranslationProjectDocsInput!) {
    updateTranslationProjectDocs(input: $input) { success errors }
  }`;
  await getGraphQLClient().request(query, { input: { projectId, ...docs } });
}

// Re-enqueues narration synthesis for every translated segment. Hash-guarded on
// the backend, so only segments whose audio is stale actually re-synthesize.
export async function regenerateAudio(projectId: string): Promise<number> {
  const query = `mutation($input: RegenerateTranslationAudioInput!) {
    regenerateTranslationAudio(input: $input) { enqueued success errors }
  }`;
  const data = await getGraphQLClient().request<{
    regenerateTranslationAudio: { enqueued: number; success: boolean; errors: string[] };
  }>(query, { input: { projectId } });
  return data.regenerateTranslationAudio.enqueued;
}

export async function translateChapter(chapterId: string): Promise<void> {
  const query = `mutation($input: TranslateTranslationChapterInput!) {
    translateTranslationChapter(input: $input) { success errors }
  }`;
  await getGraphQLClient().request(query, { input: { chapterId } });
}

export async function setAutoTranslate(projectId: string, enabled: boolean): Promise<void> {
  const query = `mutation($input: SetTranslationAutoTranslateInput!) {
    setTranslationAutoTranslate(input: $input) { success errors }
  }`;
  await getGraphQLClient().request(query, { input: { projectId, enabled } });
}

export async function autoTranslateChapter(chapterId: string): Promise<void> {
  const query = `mutation($input: AutoTranslateTranslationChapterInput!) {
    autoTranslateTranslationChapter(input: $input) { success errors }
  }`;
  await getGraphQLClient().request(query, { input: { chapterId } });
}

export async function suggestWording(input: {
  segmentId: string;
  selectedText: string;
  precedingText?: string;
  followingText?: string;
}): Promise<string[]> {
  const query = `mutation($input: SuggestTranslationWordingInput!) {
    suggestTranslationWording(input: $input) { suggestions errors }
  }`;
  const data = await getGraphQLClient().request<{
    suggestTranslationWording: { suggestions: string[]; errors: string[] };
  }>(query, { input });
  const { suggestions, errors } = data.suggestTranslationWording;
  // A populated `errors` field means the request failed (auth, missing segment,
  // AI call error) — throw so the mutation reports an error instead of looking
  // like a genuine "no suggestions" result. An empty array with no errors is a
  // valid result (the wording already reads well).
  if (errors && errors.length > 0) {
    throw new Error(errors.join('; '));
  }
  return suggestions;
}

export async function updateSegment(segmentId: string, targetText: string): Promise<void> {
  const query = `mutation($input: UpdateTranslationSegmentInput!) {
    updateTranslationSegment(input: $input) { success errors }
  }`;
  await getGraphQLClient().request(query, { input: { segmentId, targetText } });
}

export async function resolveSegmentConflict(input: {
  segmentId: string;
  choice: 'mine' | 'repo' | 'merge';
  mergedText?: string;
}): Promise<void> {
  const query = `mutation($input: ResolveTranslationSegmentConflictInput!) {
    resolveTranslationSegmentConflict(input: $input) { success errors }
  }`;
  await getGraphQLClient().request(query, { input });
}

export async function synthesizeAudio(segmentId: string): Promise<void> {
  const query = `mutation($input: SynthesizeTranslationAudioInput!) {
    synthesizeTranslationAudio(input: $input) { success errors }
  }`;
  await getGraphQLClient().request(query, { input: { segmentId } });
}

export async function syncToGithub(projectId: string): Promise<void> {
  const query = `mutation($input: SyncTranslationToGithubInput!) {
    syncTranslationToGithub(input: $input) { success errors }
  }`;
  const data = await getGraphQLClient().request<{
    syncTranslationToGithub: { success: boolean; errors: string[] };
  }>(query, { input: { projectId } });
  const result = data.syncTranslationToGithub;
  if (!result.success) {
    throw new Error(result.errors.join('; ') || 'Sync failed');
  }
}

// Downloads the project as an EPUB via the authenticated export endpoint.
export async function exportProjectEpub(
  projectId: string,
  slug: string,
  format: 'translated' | 'original' = 'translated',
): Promise<void> {
  const token = localStorage.getItem('token');
  const url = `${window.location.origin}/translate/${projectId}/export?format=${format}`;
  const response = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!response.ok) throw new Error('Export failed');

  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = `${slug}-${format}.epub`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
}

export async function saveReadingPosition(input: {
  projectId: string;
  segmentExternalId: string;
  charOffset?: number;
}): Promise<void> {
  const query = `mutation($input: SaveTranslationReadingPositionInput!) {
    saveTranslationReadingPosition(input: $input) { success errors }
  }`;
  await getGraphQLClient().request(query, { input });
}
